/**
 * What the renderer needs only in a browser: registered once at boot, beside
 * the desktop's own command registration (main.ts).
 */
import { registerAll } from '../commands/registry'
import { useThemeStore, applyTheme } from '../stores/theme'
import { chooseTheme } from '../stores/system-theme'
import { notify } from '../stores/notifications'
import { reportError } from '../utils/report'
import { activeDoc, newDoc, requestEditorFocus } from '../stores/documents'
import { handleEditAction } from './edit-actions'
import { setupPwa } from './pwa'
import { installLinkGuard } from './links'

/**
 * Markdown pasted with no document open becomes a new one: the quickest way in
 * for text copied from somewhere else, and for a browser with no file access
 * at all. A paste into a field (Open Quickly, Preferences) is left alone.
 */
function installPasteToOpen(): void {
  document.addEventListener('paste', (e) => {
    if (activeDoc.value) return
    const target = e.target
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return
    if (target instanceof HTMLElement && target.isContentEditable) return
    const text = e.clipboardData?.getData('text/plain') ?? ''
    if (!text.trim()) return
    e.preventDefault()
    const doc = newDoc()
    doc.content = text.replace(/\r\n/g, '\n')
    requestEditorFocus(doc.id)
  })
}

export function installWeb(): void {
  window.api.web.onEditAction(handleEditAction)
  installLinkGuard()
  installPasteToOpen()

  // Offline, and updates. In a dev server there is no service worker and the
  // registration does nothing; Check Updates then says so.
  let updateSW: (reload?: boolean) => Promise<void> = async () => {}
  const pwa = setupPwa((options) => {
    void import('virtual:pwa-register').then((m) => {
      updateSW = m.registerSW(options)
    })
    return (reload) => updateSW(reload)
  })

  const themes = useThemeStore()
  registerAll([
    // Replaces the desktop's greyed-out "no update server": the page is its own.
    { id: 'help.checkUpdates', run: () => pwa.checkForUpdate() },
    {
      id: 'theme.import',
      run: async () => {
        try {
          const id = await window.api.web.importTheme()
          if (!id) return
          // The list updates through the themes channel; wait for it once.
          for (let i = 0; i < 20 && !themes.available.some((t) => t.id === id); i++) {
            await new Promise((r) => setTimeout(r, 50))
          }
          await chooseTheme(id)
          notify(`Imported the theme “${themes.available.find((t) => t.id === id)?.name ?? id}”.`)
        } catch (err) {
          reportError('import theme', err)
        }
      },
    },
    {
      id: 'theme.removeImported',
      enabled: () => themes.available.some((t) => t.id === themes.current && !t.builtin),
      run: async () => {
        const id = themes.current
        const info = themes.available.find((t) => t.id === id)
        if (!info || info.builtin) return
        const sure = await window.api.app.confirm(
          `Remove the theme “${info.name}”?`,
          'It is removed from every tab of the app. Import the file again to get it back.',
          'Remove',
          'Cancel'
        )
        if (!sure) return
        await chooseTheme('github')
        await applyTheme('github')
        await window.api.web.removeTheme(id)
      },
    },
  ])
}
